// Message templates — the pure half.
//
// This module is the JS mirror of healo_backend/internal/messagetemplates: the same
// {{token}} dialect, the same render semantics, the same normalize rules. It exists
// so the composer can preview and insert a template with zero round trips — the
// server resolves the variable VALUES once per patient, and every render after that
// is local.
//
// Because it is a mirror, the two halves must move together. The Go tests in
// internal/messagetemplates/render_test.go and the vitest cases next door are
// deliberately the same cases; change one, change both.
//
// Kept free of React, stores and timezone.js on purpose — vitest runs this suite in
// a plain node environment (see vitest.config.mjs), which is what makes the rules
// worth pinning testable at all.

// One regex serves rendering and reporting, so the two can never disagree about what
// counts as a token. Mirrors placeholderRE in render.go.
const PLACEHOLDER_RE = /\{\{([^{}]*)\}\}/g;
// A line that is nothing but a WhatsApp-bold label, e.g. "*Address:* " — what
// "*Address:* {{location}}" collapses to when the clinic has no address.
const EMPTY_BOLD_LABEL_RE = /^\s*\*[^*\n]+:\*\s*$/;
const THREE_PLUS_NEWLINE_RE = /\n{3,}/g;
// The "/" trigger only fires on a draft that is exactly a slash plus word
// characters, so a URL, a date or a mid-sentence slash can never open the picker.
const SLASH_QUERY_RE = /^\/([\w-]*)$/;

/**
 * Substitute {{token}} occurrences, drop label-only lines whose value came back
 * empty, and collapse the blank lines that leaves behind. Returns plain WhatsApp text.
 *
 * Substitution is a SINGLE pass: an unknown token maps to "" (which is exactly the
 * "strip what did not resolve" rule) and a value that itself contains "{{...}}" is
 * left alone rather than re-substituted.
 *
 * @param {string} body Template body.
 * @param {Record<string,string>} vars Resolved variable values.
 * @returns {string}
 */
export function renderTemplate(body, vars) {
  const source = typeof body === "string" ? body : "";
  const values = vars && typeof vars === "object" ? vars : {};

  const substituted = source.replace(PLACEHOLDER_RE, (_match, token) => {
    const value = values[token];
    return typeof value === "string" ? value : "";
  });

  const kept = substituted
    .split("\n")
    .filter((line) => !EMPTY_BOLD_LABEL_RE.test(line))
    .map((line) => line.replace(/[ \t]+$/, ""));

  return kept.join("\n").replace(THREE_PLUS_NEWLINE_RE, "\n\n").trim();
}

/**
 * List the tokens in `body` that produce no text — either absent from `vars` or
 * blank there. Both look identical to the reader, so both are reported.
 *
 * The picker shows this so staff notice a missing detail BEFORE inserting, since
 * renderTemplate removes the evidence.
 *
 * Lookup uses the RAW inner text, exactly as renderTemplate does, so "{{ date }}"
 * (with spaces) is correctly reported as unresolved; only the display is trimmed.
 *
 * @returns {string[]} First-appearance order, deduped.
 */
export function unresolvedTokens(body, vars) {
  const source = typeof body === "string" ? body : "";
  const values = vars && typeof vars === "object" ? vars : {};

  const seen = new Set();
  const out = [];
  for (const match of source.matchAll(PLACEHOLDER_RE)) {
    const raw = match[1];
    const token = raw.trim();
    if (!token || seen.has(token)) continue;
    const value = values[raw];
    if (typeof value === "string" && value.trim() !== "") continue;
    seen.add(token);
    out.push(token);
  }
  return out;
}

function truncate(value, max) {
  const text = typeof value === "string" ? value : "";
  return Array.from(text).slice(0, max).join("");
}

// Mirrors the guardrails in storage/message_templates.go. A mismatch here would let
// the editor accept text the server silently truncates.
export const TEMPLATE_LIMITS = Object.freeze({
  title: 120,
  category: 60,
  shortcut: 24,
  body: 4000,
});

/**
 * Reduce a typed shortcut to what the "/" trigger can actually match: no leading
 * slash, lower case, and only the characters SLASH_QUERY_RE admits. Anything else
 * would be a shortcut the user could never type.
 * Mirrors normalizeTemplateShortcut in storage/message_templates.go.
 */
export function normalizeTemplateShortcut(raw) {
  const trimmed = (typeof raw === "string" ? raw : "").trim().replace(/^\//, "");
  const kept = Array.from(trimmed.toLowerCase())
    .filter((ch) => /[a-z0-9_-]/.test(ch))
    .join("");
  return truncate(kept, TEMPLATE_LIMITS.shortcut);
}

/**
 * Trim, cap lengths and clamp order so the editor's draft matches what the server
 * will store. Mirrors NormalizeMessageTemplate in storage/message_templates.go.
 */
export function normalizeTemplateDraft(draft) {
  const source = draft && typeof draft === "object" ? draft : {};
  const order = Number(source.order);
  return {
    ...source,
    title: truncate(String(source.title ?? "").trim(), TEMPLATE_LIMITS.title),
    category: truncate(String(source.category ?? "").trim(), TEMPLATE_LIMITS.category),
    body: truncate(String(source.body ?? "").trim(), TEMPLATE_LIMITS.body),
    shortcut: normalizeTemplateShortcut(source.shortcut),
    shared: Boolean(source.shared),
    order: Number.isFinite(order) && order > 0 ? Math.floor(order) : 0,
  };
}

/**
 * The query behind a "/" trigger, or null when the draft is not a bare slash command.
 *
 * Returning null for anything else is what keeps the trigger inert on URLs
 * ("https://..."), times ("8:30") and mid-sentence slashes — and what keeps it from
 * competing with the @mention dropdown, which owns the composer's arrow keys.
 *
 * @returns {string|null} "" for a lone "/", the typed query otherwise.
 */
export function parseSlashQuery(draft) {
  const source = typeof draft === "string" ? draft : "";
  const match = SLASH_QUERY_RE.exec(source);
  return match ? match[1].toLowerCase() : null;
}

/**
 * Filter templates by a picker query, matching title, shortcut and category.
 * An empty query returns everything, so the picker opens showing the whole library.
 */
export function matchTemplates(templates, query) {
  const list = Array.isArray(templates) ? templates : [];
  const needle = String(query ?? "").trim().toLowerCase();
  if (!needle) return list;
  return list.filter((template) => {
    const shortcut = String(template?.shortcut ?? "").toLowerCase();
    // An exact shortcut hit should survive any other filtering.
    if (shortcut && shortcut.startsWith(needle)) return true;
    const haystack = `${template?.title ?? ""} ${template?.category ?? ""}`.toLowerCase();
    return haystack.includes(needle);
  });
}

/**
 * Group templates into the two sections the picker and the editor both render.
 * Shared first: it is the clinic's approved wording, and the common case.
 */
export function groupTemplatesByScope(templates) {
  const list = Array.isArray(templates) ? templates : [];
  return {
    shared: list.filter((template) => template?.shared),
    personal: list.filter((template) => !template?.shared),
  };
}

// Long date, matching the Go side's friendlyAppointmentDate ("Monday, 2 January 2006").
// Assembled from parts rather than a locale string so the two never drift apart on a
// different ICU build.
function formatLongDate(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: timeZone || undefined,
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value ?? "";
  return `${get("weekday")}, ${get("day")} ${get("month")} ${get("year")}`;
}

function hourInZone(date, timeZone) {
  const value = new Intl.DateTimeFormat("en-GB", {
    timeZone: timeZone || undefined,
    hour: "numeric",
    hour12: false,
  }).format(date);
  const hour = Number.parseInt(value, 10);
  return Number.isFinite(hour) ? hour % 24 : 0;
}

/**
 * The client-sourced half of the variable map (messagetemplates.SourceClient).
 *
 * These four tokens are resolved here rather than server-side because the browser
 * knows things the server would have to guess: the user's chosen display time zone
 * (there is no tenant default in config) and which WhatsApp number the OPEN thread
 * is being answered from. The caller merges the result over the server's map.
 *
 * `timeZone` is passed in rather than read from timezone.js so this module stays
 * pure and the tests stay deterministic.
 */
export function buildClientTemplateVars({ now = new Date(), timeZone = "", whatsappNumberLabel = "" } = {}) {
  const hour = hourInZone(now, timeZone);
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  return {
    today: formatLongDate(now, timeZone),
    tomorrow: formatLongDate(tomorrow, timeZone),
    greeting: hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening",
    whatsappNumberLabel: String(whatsappNumberLabel ?? "").trim(),
  };
}
